/**
 * Wraps any route that should only be reachable by an authenticated
 * staff user.
 *
 *   - user === undefined  → still checking the session, render a
 *     subtle skeleton so we don't flash an empty page or a redirect
 *     just to bounce back on the next render.
 *   - user === null       → not authenticated, redirect to /login
 *     (preserving the originally-requested URL in ?from= so the
 *     login page can bounce back to where the user was heading).
 *   - user object         → render the children.
 *
 * Optional ``adminOnly`` prop locks the route to ``role === "admin"``;
 * a non-admin authenticated user sees a friendly "Admins only"
 * message instead of being redirected (helps Frank diagnose the
 * gating when he promotes Nicki/Arooj to admin later).
 */
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "./auth";

export default function ProtectedRoute({ children, adminOnly = false }) {
  const { user } = useAuth();
  const location = useLocation();

  if (user === undefined) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-stone-50"
        data-testid="auth-loading"
      >
        <div className="flex items-center gap-3 text-stone-500 text-sm font-mono tracking-wide">
          <span className="w-4 h-4 rounded-full border-2 border-stone-300 border-t-[#1B4F8A] animate-spin" />
          <span>Checking session…</span>
        </div>
      </div>
    );
  }

  if (user === null) {
    const next = location.pathname + location.search;
    return <Navigate to={`/login?from=${encodeURIComponent(next)}`} replace />;
  }

  if (adminOnly && user.role !== "admin") {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-stone-50 px-6"
        data-testid="auth-admin-only"
      >
        <div className="text-center max-w-sm">
          <p className="text-[10px] font-mono tracking-[0.22em] uppercase text-[#1B4F8A] mb-2">
            Admins only
          </p>
          <p className="text-slate-900 text-lg font-light mb-1">
            You do not have access to this page.
          </p>
          <p className="text-sm text-stone-500">
            Ask Frank to grant you admin role if you need it.
          </p>
        </div>
      </div>
    );
  }

  return children;
}
